import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { RESTORE_DELETED_CLIENT_MUTATION } from 'src/graphql/entities/client/client.operations';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';

type RestoreDeletedClientResponse = {
  restoreDeletedClient: {
    clientId: string;
  };
};

export const useRestoreClient = () => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { groupRules } = useGroupRules();

  const [restoreClientMutation, { loading }] = useMutation<RestoreDeletedClientResponse>(
    RESTORE_DELETED_CLIENT_MUTATION,
    {
      update: (cache, result) => {
        const clientId = result.data?.restoreDeletedClient?.clientId;
        if (!clientId) {
          return;
        }
        cache.modify({
          id: cache.identify({ __typename: 'Client', id: clientId }),
          fields: generateFieldModifiersMap({
            deletedAt: null,
            __typename: 'Client',
          }),
        });
      },
      onCompleted: () => {
        showSuccessAlert('Client was restored');
      },
      onError: (error) => {
        showErrorAlert(error.message ?? 'Could not restore client, please try again');
      },
    },
  );

  const restoreClient = useCallback(
    async (clientId: string) => {
      if (!groupRules[UserGroupRules.AccessToRestoreClients]) {
        return;
      }
      await restoreClientMutation({
        variables: {
          params: {
            clientId,
          },
        },
      });
    },
    [groupRules, restoreClientMutation],
  );

  return { restoreClient, isLoading: loading };
};
